import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { FlightSectorLeg, FlightSimulatorDetails } from "src/app/models/models";
import {
	FlightSimSummaryLayoutOptions,
	flightSimSummaryAcTypeLabel,
	flightSimSummaryDisplayValue,
	flightSimSummaryRegistrationLabel,
	flightSimSummarySectionTitle,
	flightSimSummarySectorNumber,
	flightSimSummarySectors,
	flightSimSummaryShowOtherAirport,
	hasTs1V3FlightDetailsForDisplay,
	isClassroomFlightSimSummary,
	resolveFlightSimRegistrationForSummary,
} from "../../shared-v2/flight-simulator-details.payload";

@Component({
	selector: "app-flight-simulator-summary-fields-v2",
	templateUrl: "./flight-simulator-summary-fields.component.html",
	styleUrls: ["./flight-simulator-summary-fields.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class FlightSimulatorSummaryFieldsV2Component {
	@Input() details?: FlightSimulatorDetails;

	/** When true, force TS1 v3 layout (e.g. Step 4 preview before submit). */
	@Input() useTs1V3FlightDetails = false;

	/** Admin form session catalog version from summary API (1 = legacy, 2 = ETS-3.5). */
	@Input() ets1SessionVersion?: number;

	private get layoutOptions(): FlightSimSummaryLayoutOptions {
		return {
			forceV3FlightDetails: this.useTs1V3FlightDetails,
			ets1SessionVersion: this.ets1SessionVersion,
		};
	}

	get showTs1V3FlightDetails(): boolean {
		return hasTs1V3FlightDetailsForDisplay(this.details, this.layoutOptions);
	}

	get isClassroom(): boolean {
		return isClassroomFlightSimSummary(this.details, this.layoutOptions);
	}

	get sectionTitle(): string {
		return flightSimSummarySectionTitle(this.details ?? {}, this.layoutOptions);
	}

	get registrationLabel(): string {
		return flightSimSummaryRegistrationLabel(this.details ?? {}, this.layoutOptions);
	}

	get registrationValue(): string {
		return resolveFlightSimRegistrationForSummary(this.details ?? {}, this.layoutOptions) || "-";
	}

	get acTypeLabel(): string {
		return flightSimSummaryAcTypeLabel(this.details);
	}

	acTypeValue(): string {
		return flightSimSummaryDisplayValue(this.details, "acType");
	}

	departureIcaoValue(): string {
		const fromTop = flightSimSummaryDisplayValue(this.details, "departureIcao");
		if (fromTop !== "-") return fromTop;
		const first = this.sectorLegs()[0];
		return first?.departureIcao || "-";
	}

	destinationIcaoValue(): string {
		const fromTop = flightSimSummaryDisplayValue(this.details, "destinationIcao");
		if (fromTop !== "-") return fromTop;
		const first = this.sectorLegs()[0];
		return first?.destinationIcao || "-";
	}

	otherAirportsRouteValue(): string {
		return flightSimSummaryDisplayValue(this.details, "otherAirportsRoute").toUpperCase();
	}

	airportRouteValue(): string {
		return flightSimSummaryDisplayValue(this.details, "airportRoute");
	}

	sectorNumberValue(): string {
		return flightSimSummarySectorNumber(this.details);
	}

	accumulatedSectorsValue(): string {
		return flightSimSummaryDisplayValue(this.details, "accumulatedSectors");
	}

	sectorLegs(): FlightSectorLeg[] {
		return flightSimSummarySectors(this.details);
	}

	/** Multi-sector list when more than one leg; single-leg uses separate Departure/Destination fields. */
	showMultiSectorIcaoList(): boolean {
		return this.sectorLegs().length > 1;
	}

	showOtherAirportRoute(): boolean {
		return flightSimSummaryShowOtherAirport(this.details ?? {}, this.layoutOptions);
	}
}
